import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { usePrefs, useRequestData } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';

import Slider from 'react-slick';
import PropTypes from 'prop-types';

import { galleryTrackingEvents } from '../../utilities/tracking';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import CarouselButton from './CarouselButton';
import VehicleImageCount from './VehicleImageCount';
import ImageDisplay from './ImageDisplay';
import useMedia from '../../hooks/useMedia';
import {
	resetWIAPIContent,
	replaceCarouselImageWithHTML
} from '../../utilities/wiapi-html';
import { REACT_SLICK_SPEED } from '../../global-constants';

const ImageSlider = ({
	openModal,
	onOpenModal,
	carouselIndex,
	setCarouselIndex,
	sliderRef,
	widgetElement,
	imgTitle
}) => {
	const prefs = usePrefs();
	const { widgetName, windowId } = useRequestData();
	const { imagesToDisplay } = useSelector((state) => state.media);
	const { galleryToolbarPlacement = 'bottom-left' } = prefs;
	const actionRef = useRef(null);
	const touchStart = useRef(null);
	const touchEnd = useRef(null);
	const isDesktop = useMedia('(min-width: 576px)', true);

	const isMobile = !isDesktop;
	const TOTAL_IMAGES = imagesToDisplay.length;
	const lastImageIndex = imagesToDisplay.length - 1;

	const showArrows = !isMobile && TOTAL_IMAGES > 1;

	const getMoveToPhotoText = (index) => {
		return TOTAL_IMAGES === index + 1
			? 'Move to last photo'
			: `Move to photo ${index + 1}`;
	};

	const handleTouchStart = (e) => {
		touchStart.current = e.touches[0].clientX;
		touchEnd.current = null;
	};

	const handleTouchEnd = () => {
		if (!touchStart.current || !touchEnd.current) return;
		const swipeDistance = touchStart.current - touchEnd.current;
		if (swipeDistance > 50) {
			actionRef.current = 'swipeLeft';
		} else if (swipeDistance < -50) {
			actionRef.current = 'swipeRight';
		}
	};

	const onNext = () => {
		const nextIndex =
			carouselIndex + 1 > lastImageIndex ? 0 : carouselIndex + 1;
		galleryTrackingEvents.clickNextButton(widgetName, windowId, {
			result: getMoveToPhotoText(nextIndex),
			currentItem: carouselIndex + 1,
			totalItems: TOTAL_IMAGES
		});
		sliderRef.current.slickNext();
	};

	const onPrev = () => {
		const nextIndex =
			carouselIndex - 1 < 0 ? lastImageIndex : carouselIndex - 1;
		galleryTrackingEvents.clickPrevButton(widgetName, windowId, {
			result: getMoveToPhotoText(nextIndex),
			currentItem: carouselIndex + 1,
			totalItems: TOTAL_IMAGES
		});
		sliderRef.current.slickPrev();
	};

	const onPhotoClick = (imageItem) => {
		if (imageItem.type === 'html' && widgetElement) {
			resetWIAPIContent(widgetElement);
			replaceCarouselImageWithHTML(widgetElement, imageItem);
		} else if (onOpenModal) {
			onOpenModal();
			galleryTrackingEvents.openModal(widgetName, windowId, {
				currentItem: carouselIndex + 1,
				totalItems: TOTAL_IMAGES
			});
		}
	};

	const mediaCount = TOTAL_IMAGES;

	// Fix adaptiveHeight for modal initial slide - wait for image to load then set height
	useEffect(() => {
		if (!openModal || !sliderRef.current) return undefined;

		const adjustHeightFrameId = requestAnimationFrame(() => {
			const slickList = sliderRef.current?.innerSlider?.list;
			if (!slickList) return;

			const currentSlide = slickList.querySelector(
				`[data-index="${carouselIndex}"]`
			);
			if (!currentSlide) return;

			const img = currentSlide.querySelector('img');
			if (!img) return;

			const setHeight = () => {
				const height = currentSlide.offsetHeight;
				if (height > 0) {
					slickList.style.height = `${height}px`;
				}
			};

			if (img.complete) {
				setHeight();
			} else {
				img.onload = setHeight;
			}
		});

		return () => cancelAnimationFrame(adjustHeightFrameId);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on modal toggle; other deps are DOM refs
	}, [openModal]);

	const settings = {
		className: 'carousel',
		infinite: true,
		speed: REACT_SLICK_SPEED,
		lazyLoad: false,
		slidesToShow: 1,
		slidesToScroll: 1,
		adaptiveHeight: true,
		arrows: showArrows,
		initialSlide: carouselIndex,
		prevArrow: (
			<CarouselButton
				direction="left"
				clickEvent={onPrev}
				mouseUpEvent={(e) => e.target.blur()}
				classes={['previous']}
			/>
		),
		nextArrow: (
			<CarouselButton
				direction="right"
				clickEvent={onNext}
				mouseUpEvent={(e) => e.target.blur()}
				classes={['next']}
			/>
		),
		beforeChange: (_current, next) => {
			if (isMobile) {
				// iOS issue with swapping back to slide before change occurred
				// need to wait to change state until change has completed
				setTimeout(() => {
					setCarouselIndex(next);
				}, REACT_SLICK_SPEED);
			} else {
				setCarouselIndex(next);
			}

			resetWIAPIContent(widgetElement);
		}
	};

	const slides = imagesToDisplay.map((imageItem, index) => {
		const imageComponent = (
			<ImageDisplay
				image={imageItem}
				index={index}
				key={imageItem.src}
				keyName="gallery-carousel"
				onClick={() => onPhotoClick(imageItem)}
				imgTitle={imgTitle}
				carouselIndex={carouselIndex}
			/>
		);
		return imageComponent;
	});

	let positionToolbar;
	let styleImageCount;
	if (openModal) {
		positionToolbar =
			'pointer-events-none position-absolute ddc-push6 mt-4 translate-left-50 btn-unstyled text-muted';
	} else {
		positionToolbar =
			galleryToolbarPlacement === 'top-left'
				? 'absolute-top ml-4 mt-4'
				: 'absolute-bottom ml-4 mb-4';
		// eslint-disable max-len
		styleImageCount =
			'vehicle-gallery-toolbar vehicle-gallery-toolbar-bg ' +
			'text-white font-weight-bold border-radius-sm py-2 px-3 ddc-font-size-xsmall border-0 text-body';
	}

	useEffect(() => {
		if (
			actionRef.current === 'swipeLeft' ||
			actionRef.current === 'swipeRight'
		) {
			galleryTrackingEvents.swipeCarousel(widgetName, windowId, {
				result: getMoveToPhotoText(carouselIndex),
				currentItem: carouselIndex,
				totalItems: TOTAL_IMAGES,
				carouselDirection:
					actionRef.current === 'swipeLeft' ? 'next' : 'previous'
			});
		}
		actionRef.current = null;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- other deps are stable refs/constants
	}, [carouselIndex, TOTAL_IMAGES]);

	if (TOTAL_IMAGES === 0) {
		return null;
	}

	return (
		<div
			className="slider-container position-relative mx-auto"
			data-testid="slider-container"
			onTouchStart={handleTouchStart}
			onTouchMove={(e) => {
				touchEnd.current = e.touches[0].clientX;
			}}
			onTouchEnd={handleTouchEnd}
		>
			<Slider ref={sliderRef} {...settings}>
				{slides}
			</Slider>
			{openModal && !isMobile ? (
				<VehicleImageCount
					className={setClassNames([
						positionToolbar,
						styleImageCount
					])}
					mediaCount={mediaCount}
					imageIndex={carouselIndex + 1}
					onClick={() => {
						onOpenModal();
						galleryTrackingEvents.clickPhotoCountToolbar(
							widgetName,
							windowId,
							{
								elementCTA: `${
									carouselIndex + 1
								} / ${TOTAL_IMAGES}`
							}
						);
					}}
				/>
			) : (
				''
			)}
		</div>
	);
};

ImageSlider.propTypes = {
	openModal: PropTypes.bool,
	onOpenModal: PropTypes.func,
	carouselIndex: PropTypes.number.isRequired,
	setCarouselIndex: PropTypes.func.isRequired,
	// sliderRef is a ref to Slider component instance
	// eslint-disable-next-line react/forbid-prop-types
	sliderRef: PropTypes.object,
	widgetElement: PropTypes.instanceOf(Element),
	imgTitle: PropTypes.string
};

export default ImageSlider;
